export type Product = {
  id: string;
  name: string;
  slug: string;
  description: string;
  price: number;
  image_url: string;
  category: string;
  age_range: string;
  stock: number;
  created_at: string;
  updated_at: string;
};

export type CartItem = {
  id: string;
  product_id: string;
  quantity: number;
  added_at: string;
};

export type OrderItem = {
  product_id: string;
  quantity: number;
  price_at_purchase: number;
};

export type Order = {
  id: string;
  buyer_name: string;
  buyer_phone: string;
  items: OrderItem[];
  total_amount: number;
  status: 'pending' | 'confirmed' | 'shipped' | 'cancelled';
  created_at: string;
  updated_at: string;
};

type DbResult<T> = { data: T; error: string | null };

const STORAGE_PREFIX = 'upbo_db_';

function getKey(table: string): string {
  return STORAGE_PREFIX + table;
}

function readRows<T>(table: string): T[] {
  if (typeof window === 'undefined') return [];
  const raw = window.localStorage.getItem(getKey(table));
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as T[]) : [];
  } catch {
    return [];
  }
}

function writeRows<T>(table: string, rows: T[]): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(getKey(table), JSON.stringify(rows));
}

function createId(): string {
  const randomPart = Math.random().toString(36).slice(2, 10);
  return Date.now().toString(36) + '_' + randomPart;
}

function matchCondition(row: any, condition: Record<string, unknown>): boolean {
  return Object.entries(condition).every(([key, value]) => row[key] === value);
}

export const db = {
  async select<T = any>(table: string): Promise<DbResult<T[]>> {
    try {
      const rows = readRows<T>(table);
      return { data: rows, error: null };
    } catch (err) {
      return { data: [], error: err instanceof Error ? err.message : 'Unable to read table' };
    }
  },

  async insert<T extends { id?: string }>(table: string, row: T): Promise<DbResult<T | null>> {
    try {
      const rows = readRows<T>(table);
      const newRow: T = { ...row, id: row.id ?? createId() };
      rows.push(newRow);
      writeRows(table, rows);
      return { data: newRow, error: null };
    } catch (err) {
      return { data: null, error: err instanceof Error ? err.message : 'Unable to insert row' };
    }
  },

  async update<T extends { id?: string }>(
    table: string,
    changes: Partial<T>,
    condition: Record<string, unknown>
  ): Promise<DbResult<T[]>> {
    try {
      if (!Object.keys(condition).length) {
        return { data: [], error: 'Update condition is required' };
      }

      const rows = readRows<T>(table);
      const updatedRows: T[] = [];
      const nextRows = rows.map((row) => {
        if (matchCondition(row, condition)) {
          const merged = { ...row, ...changes } as T;
          updatedRows.push(merged);
          return merged;
        }
        return row;
      });

      writeRows(table, nextRows);
      return { data: updatedRows, error: null };
    } catch (err) {
      return { data: [], error: err instanceof Error ? err.message : 'Unable to update rows' };
    }
  },

  async delete(table: string, condition: Record<string, unknown>): Promise<DbResult<{ deleted: number }>> {
    try {
      if (!Object.keys(condition).length) {
        return { data: { deleted: 0 }, error: 'Delete condition is required' };
      }

      const rows = readRows(table);
      const remaining: unknown[] = [];
      let deleted = 0;

      for (const row of rows) {
        if (matchCondition(row, condition)) {
          deleted += 1;
        } else {
          remaining.push(row);
        }
      }

      writeRows(table, remaining);
      return { data: { deleted }, error: null };
    } catch (err) {
      return { data: { deleted: 0 }, error: err instanceof Error ? err.message : 'Unable to delete rows' };
    }
  },
};

export async function seedMock<T extends { id?: string }>(
  table: string,
  rows: T[]
): Promise<DbResult<T[]>> {
  try {
    const existing = readRows<T>(table);
    if (existing.length > 0) {
      return { data: existing, error: null };
    }

    const seededRows = rows.map((row) => ({
      ...row,
      id: row.id ?? createId(),
    }));

    writeRows(table, seededRows);
    return { data: seededRows, error: null };
  } catch (err) {
    return { data: [], error: err instanceof Error ? err.message : 'Unable to seed table' };
  }
}
